import { z } from 'zod';

import {
  actorSnapshotSchema,
  entityIdSchema,
  nonEmptyTextSchema,
  sha256Schema,
  utcDateTimeSchema,
} from './primitives';
import { documentKindSchema } from './document-envelope';
import { personaSchema, resourceScopeSchema } from './tenant-rbac';

export const approvalStatusSchema = z.enum([
  'not-submitted',
  'submitted',
  'in-progress',
  'approved',
  'rejected',
  'withdrawn',
]);

export const approvalStageStrategySchema = z.enum(['sequential', 'parallel-any', 'parallel-all']);
export const approvalStageStatusSchema = z.enum([
  'pending',
  'active',
  'approved',
  'rejected',
  'skipped',
]);

export const approvalEligibilitySchema = z.discriminatedUnion('kind', [
  z
    .object({ kind: z.literal('named-identity'), identityIds: z.array(entityIdSchema).min(1) })
    .strict(),
  z
    .object({
      kind: z.literal('persona'),
      personas: z.array(personaSchema).min(1),
      scopeRelation: z.enum(['same-resource-scope', 'tenant-headquarters']),
    })
    .strict(),
  z
    .object({
      kind: z.literal('duty-position'),
      dutyCodes: z.array(nonEmptyTextSchema.max(100)).min(1),
      scopeRelation: z.enum(['same-resource-scope', 'tenant-headquarters']),
    })
    .strict(),
]);

export const approvalMandateStageSchema = z
  .object({
    mandateStageId: entityIdSchema,
    sequence: z.number().int().positive(),
    label: nonEmptyTextSchema.max(100),
    strategy: approvalStageStrategySchema,
    minimumApprovals: z.number().int().positive(),
    eligibility: approvalEligibilitySchema,
  })
  .strict();

export const approvalMandateSchema = z
  .object({
    mandateId: entityIdSchema,
    tenantId: entityIdSchema,
    title: nonEmptyTextSchema.max(200),
    documentKinds: z.array(documentKindSchema).min(1),
    scopeKinds: z.array(z.enum(['tenant', 'site', 'contractor'])).min(1),
    allowSelfApproval: z.boolean(),
    stages: z.array(approvalMandateStageSchema).min(1),
    validFrom: utcDateTimeSchema,
    validUntil: utcDateTimeSchema.nullable(),
    version: z.number().int().positive(),
  })
  .strict()
  .superRefine((mandate, context) => {
    const sequences = mandate.stages.map((stage) => stage.sequence);
    if (new Set(sequences).size !== sequences.length) {
      context.addIssue({
        code: 'custom',
        path: ['stages'],
        message: 'Mandate stage sequence values must be unique',
      });
    }
    mandate.stages.forEach((stage, index) => {
      if (stage.strategy === 'parallel-all' && stage.eligibility.kind === 'named-identity') {
        if (stage.minimumApprovals !== stage.eligibility.identityIds.length) {
          context.addIssue({
            code: 'custom',
            path: ['stages', index, 'minimumApprovals'],
            message: 'parallel-all named stages must require every named identity',
          });
        }
      }
      if (stage.strategy === 'parallel-any' && stage.minimumApprovals !== 1) {
        context.addIssue({
          code: 'custom',
          path: ['stages', index, 'minimumApprovals'],
          message: 'parallel-any stages require exactly one approval',
        });
      }
    });
    if (
      mandate.validUntil !== null &&
      Date.parse(mandate.validUntil) <= Date.parse(mandate.validFrom)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['validUntil'],
        message: 'validUntil must be after validFrom',
      });
    }
  });

export const approvalAppointmentSchema = z
  .object({
    identityId: entityIdSchema,
    mandateStageId: entityIdSchema,
    source: z.enum(['named-identity', 'persona', 'duty-position', 'delegation']),
    delegatedByIdentityId: entityIdSchema.nullable(),
    delegationEvidenceId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((appointment, context) => {
    const delegated = appointment.source === 'delegation';
    if (
      delegated !==
      (appointment.delegatedByIdentityId !== null && appointment.delegationEvidenceId !== null)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['delegationEvidenceId'],
        message: 'Delegated appointments require delegator and evidence',
      });
    }
  });

export const approvalStageSchema = z
  .object({
    stageId: entityIdSchema,
    mandateStageId: entityIdSchema,
    sequence: z.number().int().positive(),
    label: nonEmptyTextSchema.max(100),
    strategy: approvalStageStrategySchema,
    appointments: z.array(approvalAppointmentSchema).min(1),
    status: approvalStageStatusSchema,
  })
  .strict();

export const approvalInstanceSchema = z
  .object({
    approvalId: entityIdSchema,
    documentId: entityIdSchema,
    documentVersionId: entityIdSchema,
    documentContentHash: sha256Schema,
    scope: resourceScopeSchema,
    mandateId: entityIdSchema,
    mandateVersion: z.number().int().positive(),
    round: z.number().int().positive(),
    previousApprovalId: entityIdSchema.nullable(),
    status: approvalStatusSchema,
    stages: z.array(approvalStageSchema).min(1),
    submittedBy: actorSnapshotSchema.nullable(),
    submittedAt: utcDateTimeSchema.nullable(),
    completedAt: utcDateTimeSchema.nullable(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((approval, context) => {
    if (approval.round === 1 && approval.previousApprovalId !== null) {
      context.addIssue({
        code: 'custom',
        path: ['previousApprovalId'],
        message: 'The first approval round cannot have a previous approval',
      });
    }
    if (approval.round > 1 && approval.previousApprovalId === null) {
      context.addIssue({
        code: 'custom',
        path: ['previousApprovalId'],
        message: 'A resubmission must reference the previous approval round',
      });
    }

    if (approval.status !== 'not-submitted') {
      if (approval.submittedBy === null || approval.submittedAt === null) {
        context.addIssue({
          code: 'custom',
          path: ['submittedAt'],
          message: 'A submitted approval requires submitter evidence and submittedAt',
        });
      }
    }

    const completed = ['approved', 'rejected', 'withdrawn'].includes(approval.status);
    if (completed !== (approval.completedAt !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['completedAt'],
        message: 'completedAt must be present only for terminal approval statuses',
      });
    }

    const sequences = approval.stages.map((stage) => stage.sequence);
    if (new Set(sequences).size !== sequences.length) {
      context.addIssue({
        code: 'custom',
        path: ['stages'],
        message: 'Approval stage sequence values must be unique',
      });
    }
  });

export const approvalActionKindSchema = z.enum([
  'submit',
  'begin-review',
  'approve',
  'reject',
  'withdraw',
  'resubmit',
]);

export const approvalActionSchema = z
  .object({
    actionId: entityIdSchema,
    action: approvalActionKindSchema,
    actor: actorSnapshotSchema,
    occurredAt: utcDateTimeSchema,
    reason: z.string().trim().max(2_000).nullable(),
    expectedDocumentVersionId: entityIdSchema,
    expectedDocumentContentHash: sha256Schema,
    idempotencyKey: entityIdSchema,
    online: z.literal(true),
  })
  .strict()
  .superRefine((action, context) => {
    if (['reject', 'withdraw'].includes(action.action) && !action.reason) {
      context.addIssue({
        code: 'custom',
        path: ['reason'],
        message: `${action.action} requires a reason`,
      });
    }
  });

export type ApprovalAction = z.infer<typeof approvalActionKindSchema>;
export type ApprovalInstance = z.infer<typeof approvalInstanceSchema>;
export type ApprovalMandate = z.infer<typeof approvalMandateSchema>;
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

const approvalTransitions = {
  'not-submitted': { submit: 'submitted' },
  submitted: { 'begin-review': 'in-progress', withdraw: 'withdrawn' },
  'in-progress': { approve: 'approved', reject: 'rejected', withdraw: 'withdrawn' },
  approved: {},
  rejected: { resubmit: 'submitted' },
  withdrawn: { resubmit: 'submitted' },
} as const satisfies Record<ApprovalStatus, Partial<Record<ApprovalAction, ApprovalStatus>>>;

export function nextApprovalStatus(
  status: ApprovalStatus,
  action: ApprovalAction,
): ApprovalStatus | null {
  return (
    approvalTransitions[status][action as keyof (typeof approvalTransitions)[typeof status]] ?? null
  );
}

export function canTransitionApproval(status: ApprovalStatus, action: ApprovalAction): boolean {
  return nextApprovalStatus(status, action) !== null;
}
